import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { DotsThree, Eye, GearSix, Prohibit, CheckCircle } from '@phosphor-icons/react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { orgApi } from '@/api/organizations'
import { toast } from '@/hooks/use-toast'
import type { Organization } from '@/api/organizations'

interface OrgRowActionsProps {
  org: Organization
}

export function OrgRowActions({ org }: OrgRowActionsProps) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [showDialog, setShowDialog] = useState(false)

  const toggleActiveMutation = useMutation({
    mutationFn: () => orgApi.update(org.id, { is_active: !org.is_active }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizations'] })
      toast({
        title: org.is_active ? 'Organization deactivated' : 'Organization activated',
        description: `${org.name} has been ${org.is_active ? 'deactivated' : 'activated'}.`,
      })
      setShowDialog(false)
    },
    onError: () => {
      toast({
        title: 'Error',
        description: `Failed to ${org.is_active ? 'deactivate' : 'activate'} ${org.name}.`,
        variant: 'destructive',
      })
    },
  })

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={(e) => e.stopPropagation()}
          >
            <DotsThree className="h-4 w-4" weight="bold" />
            <span className="sr-only">Open menu</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
          <DropdownMenuItem onClick={() => navigate(`/organizations/${org.id}`)}>
            <Eye className="mr-2 h-4 w-4" />
            View
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => navigate(`/organizations/${org.id}?tab=settings`)}>
            <GearSix className="mr-2 h-4 w-4" />
            Edit Settings
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() => setShowDialog(true)}
            className={org.is_active ? 'text-destructive focus:text-destructive' : ''}
          >
            {org.is_active ? (
              <>
                <Prohibit className="mr-2 h-4 w-4" />
                Deactivate
              </>
            ) : (
              <>
                <CheckCircle className="mr-2 h-4 w-4" />
                Activate
              </>
            )}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog open={showDialog} onOpenChange={setShowDialog}>
        <AlertDialogContent onClick={(e) => e.stopPropagation()}>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {org.is_active ? 'Deactivate Organization' : 'Activate Organization'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {org.is_active
                ? `Are you sure you want to deactivate ${org.name}? Users will lose access.`
                : `Are you sure you want to activate ${org.name}?`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => toggleActiveMutation.mutate()}
              className={org.is_active ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90' : ''}
              disabled={toggleActiveMutation.isPending}
            >
              {toggleActiveMutation.isPending
                ? 'Processing...'
                : org.is_active
                  ? 'Deactivate'
                  : 'Activate'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
